import React, { useState, useMemo, useRef, useCallback } from 'react';
import { Modal, Table, Radio, DatePicker, Row, Col, Typography, Space, Card, Button, Tooltip, message, Tabs } from 'antd';
import { CalendarOutlined, ExportOutlined, FileExcelOutlined, AimOutlined } from '@ant-design/icons';
import * as XLSX from 'xlsx';
import TargetSetupTab from './TargetSetupTab';
import dayjs, { Dayjs } from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import isBetween from 'dayjs/plugin/isBetween';
import { readCache } from '@/store';
import type { TopUpData } from '@/services/topup';
import type { RevenueItem } from '@/services';
import { useMonthlyTarget } from '@/services';

dayjs.extend(isoWeek);
dayjs.extend(weekOfYear);
dayjs.extend(isBetween);

interface SummaryModalProps {
  baselineForecasts?: RevenueItem[];
  visible: boolean;
  onClose: () => void;
  events: API.AppEventData[];
}

type FilterType = 'month' | 'week';

interface SummaryTableData {
  key: string;
  date: string;
  eventName: string;
  predictedRevenue: number;
  actualRevenue: number;
  dateRowSpan?: number;
  revenueRowSpan?: number;
  isMarked?: boolean;
}

const { Title } = Typography;

const SummaryModal: React.FC<SummaryModalProps> = ({
  baselineForecasts = [],
  visible,
  onClose,
  events,
}) => {
  const [filterType, setFilterType] = useState<FilterType>('month');
  const [selectedDate, setSelectedDate] = useState<Dayjs>(dayjs());
  const [activeTab, setActiveTab] = useState<string>('summary');

  // Get monthly target for the current selected month (only when filter is month)
  const { data: monthlyTargetData, isLoading: targetLoading } = useMonthlyTarget(
    selectedDate.year(),
    selectedDate.month() + 1 // dayjs months are 0-based
  );

  // Only use target data when filter type is month
  const monthlyTarget = filterType === 'month' ? monthlyTargetData?.amount || null : null;

  // Get topup data from cache
  const topupData = useMemo(() => {
    const cachedData = readCache<string[], TopUpData[]>(['topup']);
    return cachedData || [];
  }, []);

  // Get date range based on filter type
  const getDateRange = useMemo(() => {
    if (filterType === 'month') {
      const startOfMonth = selectedDate.startOf('month');
      const endOfMonth = selectedDate.endOf('month');
      return { start: startOfMonth, end: endOfMonth };
    } else {
      // Week from Monday to Sunday
      const startOfWeek = selectedDate.startOf('isoWeek');
      const endOfWeek = selectedDate.endOf('isoWeek');
      return { start: startOfWeek, end: endOfWeek };
    }
  }, [filterType, selectedDate]);

  // Filter events based on selected period
  const filteredEvents = useMemo(() => {
    return events.filter(event => {
      if (!event.startDate || !event.endDate) return false;
      
      const eventStartDate = dayjs(event.startDate);
      const eventEndDate = dayjs(event.endDate);
      
      // Validate that the dates are valid
      if (!eventStartDate.isValid() || !eventEndDate.isValid()) {
        return false;
      }
      
      // Check if event has any overlap with the selected period
      // Event overlaps if: event.startDate <= period.endDate AND event.endDate >= period.startDate
      return (eventStartDate.isBefore(getDateRange.end, 'day') || eventStartDate.isSame(getDateRange.end, 'day')) && 
             (eventEndDate.isAfter(getDateRange.start, 'day') || eventEndDate.isSame(getDateRange.start, 'day'));
    });
  }, [events, getDateRange]);

  // Helper function to get topup amount for a specific date
  const getTopupAmountForDate = useMemo(() => {
    const topupMap = new Map<string, number>();
    
    topupData.forEach(topup => {
      if (topup.date) {
        const dateKey = dayjs(topup.date).format('YYYY-MM-DD');
        topupMap.set(dateKey, (topupMap.get(dateKey) || 0) + (topup.amount || 0));
      }
    });
    
    return topupMap;
  }, [topupData]);

  // Generate table data with rowspan logic
  const tableData = useMemo(() => {
    const dateMap = new Map<string, API.AppEventData[]>();
    
    // Group events by each day they occur (from start to end date)
    filteredEvents.forEach(event => {
      if (!event.startDate || !event.endDate) return;
      
      const startDate = dayjs(event.startDate);
      const endDate = dayjs(event.endDate);
      
      if (!startDate.isValid() || !endDate.isValid()) return;
      
      // Add event to every day it occurs
      let currentDate = startDate;
      while (currentDate.isBefore(endDate) || currentDate.isSame(endDate, 'day')) {
        const dateKey = currentDate.format('YYYY-MM-DD');
        
        // Only include dates within the selected period
        if (currentDate.isBetween(getDateRange.start, getDateRange.end, 'day', '[]')) {
          if (!dateMap.has(dateKey)) {
            dateMap.set(dateKey, []);
          }
          dateMap.get(dateKey)!.push(event);
        }
        
        currentDate = currentDate.add(1, 'day');
      }
    });

    // Generate all dates in the range
    const allDates: string[] = [];
    let current = getDateRange.start;
    while (current.isBefore(getDateRange.end) || current.isSame(getDateRange.end, 'day')) {
      allDates.push(current.format('YYYY-MM-DD'));
      current = current.add(1, 'day');
    }

    const tableRows: SummaryTableData[] = [];

    allDates.forEach(dateKey => {
      const eventsForDate = dateMap.get(dateKey) || [];
      
      // Get topup amount for this date (this will be our actual revenue)
      const topupAmount = getTopupAmountForDate.get(dateKey) || 0;
      
      if (eventsForDate.length === 0) {
        // No events for this date, but show topup amount if available
        tableRows.push({
          key: `${dateKey}-empty`,
          date: dayjs(dateKey).format('DD/MM/YYYY (dddd)'),
          eventName: 'No events',
          predictedRevenue: 0,
          actualRevenue: topupAmount, // Use topup amount as actual revenue
          dateRowSpan: 1,
          revenueRowSpan: 1,
        });
      } else {
        // Calculate total predicted revenue for this date from all events happening on this date
        const eventRevenueMap = new Map<number, { predicted: number }>();
        
        // Group by unique events to avoid double counting
        eventsForDate.forEach(event => {
          if (eventRevenueMap.has(event.id)) return; // Skip if already processed
          
          // Calculate predicted revenue for this event based on the current date
          let dailyPredicted = 0;
          const currentDateStr = dayjs(dateKey).format('YYYY-MM-DD');
          const eventStart = dayjs(event.startDate);
          const eventEnd = dayjs(event.endDate);
          
          // Calculate which day index this is within the event (0-based)
          const currentDate = dayjs(dateKey);
          const dayIndex = currentDate.diff(eventStart, 'day');
          
          // Get predicted revenue for this specific day from forecastSpending array
          // forecastSpending is always an array where each index represents a day of the event
          if (event.forecastSpending && dayIndex >= 0 && dayIndex < event.forecastSpending.length) {
            dailyPredicted = event.forecastSpending[dayIndex] || 0;
          }
          
          eventRevenueMap.set(event.id, {
            predicted: dailyPredicted
          });
        });
        
        // Sum all unique events' predicted revenue for this date
        const totalPredicted = Array.from(eventRevenueMap.values()).reduce((sum, revenue) => 
          sum + revenue.predicted, 0
        );

        // Get unique events for this date to avoid duplicates
        const uniqueEvents = Array.from(new Map(eventsForDate.map(event => [event.id, event])).values());
        
        // Add rows for each unique event
        uniqueEvents.forEach((event, index) => {
          const eventDuration = dayjs(event.endDate).diff(dayjs(event.startDate), 'day') + 1;

          const addedBaselineRevenue = baselineForecasts?.find(baseline => baseline.date === dateKey)?.amount || 0;
          
          tableRows.push({
            key: `${dateKey}-${event.id}`,
            date: dayjs(dateKey).format('DD/MM/YYYY (dddd)'),
            eventName: `${event.name || 'Untitled Event'} (${eventDuration} day${eventDuration > 1 ? 's' : ''})`,
            predictedRevenue: totalPredicted + addedBaselineRevenue,
            actualRevenue: topupAmount, // Use topup amount as actual revenue for all events on this date
            dateRowSpan: index === 0 ? uniqueEvents.length : 0,
            revenueRowSpan: index === 0 ? uniqueEvents.length : 0,
            isMarked: dayjs(dateKey).format('DD/MM/YYYY') === dayjs(event?.startDate).format('DD/MM/YYYY'),
          });
        });
      }
    });

    return tableRows;
  }, [filteredEvents, getDateRange, getTopupAmountForDate]);

  // Calculate sum values for column headers
  const sumValues = useMemo(() => {
    // Use a Set to track unique date-revenue combinations to avoid double counting
    const uniqueRevenues = new Set<string>();
    let totalPredicted = 0;
    let totalActual = 0;
    
    tableData.forEach(row => {
      // Only count revenue for the first row of each date (when rowSpan > 0)
      if (row.revenueRowSpan && row.revenueRowSpan > 0) {
        const uniqueKey = `${row.date}-${row.actualRevenue}`;
        if (!uniqueRevenues.has(uniqueKey)) {
          uniqueRevenues.add(uniqueKey);
          totalPredicted += row.predictedRevenue;
          totalActual += row.actualRevenue;
        }
      }
    });
    
    return {
      totalPredicted,
      totalActual
    };
  }, [tableData]);

  // Calculate progression percentage
  const progressionData = useMemo(() => {
    if (filterType !== 'month' || !monthlyTarget || monthlyTarget <= 0) {
      return null;
    }

    const actualRevenue = sumValues.totalActual;
    const percentage = (actualRevenue / monthlyTarget) * 100;
    
    return {
      actualRevenue,
      targetRevenue: monthlyTarget,
      percentage: Math.round(percentage * 100) / 100, // Round to 2 decimal places
    };
  }, [filterType, monthlyTarget, sumValues.totalActual]);

  // Helper functions for date formatting
  const getDatePickerFormat = () => {
    return filterType === 'month' ? 'YYYY-MM' : 'YYYY-MM-DD';
  };

  const getDatePickerPicker = () => {
    return filterType === 'month' ? 'month' : 'week';
  };

  const getDateRangeText = () => {
    if (filterType === 'month') {
      return selectedDate.format('MMMM YYYY');
    } else {
      return `${getDateRange.start.format('DD/MM/YYYY')} - ${getDateRange.end.format('DD/MM/YYYY')}`;
    }
  };

  // Prepare Excel data for export
  const excelData = useMemo(() => {
    // Create array of arrays for XLSX
    const data: any[][] = [];
    const rowMetadata: Array<{ isMarked?: boolean; isTotal?: boolean }> = [];

    // Add header row
    data.push([
      'Date',
      'Events',
      `Predicted Revenue\nTotal: ${sumValues.totalPredicted.toLocaleString()}`,
      `Actual Revenue (TopUp)\nTotal: ${sumValues.totalActual.toLocaleString()}`
    ]);
    rowMetadata.push({ isTotal: false });

    // Process table data with proper logic for Excel
    const dateGroups = new Map<string, typeof tableData>();
    
    tableData.forEach(row => {
      if (!dateGroups.has(row.date)) {
        dateGroups.set(row.date, []);
      }
      dateGroups.get(row.date)!.push(row);
    });

    dateGroups.forEach((rows, date) => {
      if (rows.length === 0) return;

      // Find events that actually START on this date (marked events)
      const eventsStartingOnDate = rows.filter(row => row.isMarked);
      const firstRow = rows[0];

      if (eventsStartingOnDate.length === 0) {
        // No events starting on this date
        data.push([
          firstRow.date,
          'No events starting',
          firstRow.predictedRevenue,
          firstRow.actualRevenue
        ]);
        rowMetadata.push({ isMarked: false });
      } else if (eventsStartingOnDate.length === 1) {
        // Single event starting on this date
        const eventRow = eventsStartingOnDate[0];
        data.push([
          eventRow.date,
          eventRow.eventName,
          eventRow.predictedRevenue,
          eventRow.actualRevenue
        ]);
        rowMetadata.push({ isMarked: eventRow.isMarked });
      } else {
        // Multiple events starting on the same date
        const eventNames = eventsStartingOnDate.map(row => row.eventName).join('\n');
        const firstEvent = eventsStartingOnDate[0];
        
        data.push([
          firstEvent.date,
          eventNames,
          firstEvent.predictedRevenue,
          firstEvent.actualRevenue
        ]);
        rowMetadata.push({ isMarked: true });
      }
    });

    // Add summary row
    data.push([
      'TOTAL',
      '',
      sumValues.totalPredicted,
      sumValues.totalActual
    ]);
    rowMetadata.push({ isTotal: true });

    return { data, rowMetadata };
  }, [tableData, sumValues]);

  // Generate Excel filename with current date and filter type
  const excelFilename = useMemo(() => {
    const dateStr = getDateRangeText().replace(/[^\w\s-]/g, '').replace(/\s+/g, '_');
    return `Revenue_Summary_${filterType}_${dateStr}_${dayjs().format('YYYY-MM-DD')}.xlsx`;
  }, [filterType, selectedDate, getDateRange]);

  // Function to export data to Excel using XLSX
  const handleExcelExport = useCallback(() => {
    try {
      // Create a new workbook and worksheet
      const workbook = XLSX.utils.book_new();
      const worksheet = XLSX.utils.aoa_to_sheet(excelData.data);

      // Set column widths for better display
      const columnWidths = [
        { wch: 25 }, // Date column
        { wch: 40 }, // Event Name column
        { wch: 20 }, // Predicted Revenue column
        { wch: 25 }  // Actual Revenue column
      ];
      worksheet['!cols'] = columnWidths;

      // Add cell styling for headers and totals
      const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
      
      // Style header row (row 0)
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: 0, c: col });
        if (!worksheet[cellAddress]) continue;
        
        worksheet[cellAddress].s = {
          font: { bold: true, sz: 12 },
          fill: { fgColor: { rgb: "E6F3FF" } },
          border: {
            top: { style: "thin" },
            bottom: { style: "thin" },
            left: { style: "thin" },
            right: { style: "thin" }
          },
          alignment: { horizontal: "center", vertical: "center", wrapText: true }
        };
      }

      // Style total row (last row)
      const totalRowIndex = range.e.r;
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellAddress = XLSX.utils.encode_cell({ r: totalRowIndex, c: col });
        if (!worksheet[cellAddress]) continue;
        
        worksheet[cellAddress].s = {
          font: { bold: true, sz: 11 },
          fill: { fgColor: { rgb: "FFE6E6" } },
          border: {
            top: { style: "thick" },
            bottom: { style: "thin" },
            left: { style: "thin" },
            right: { style: "thin" }
          },
          alignment: { horizontal: col === 0 ? "center" : "right", vertical: "center" }
        };
      }

      // Style marked events (events starting on each date)
      for (let row = 1; row < totalRowIndex; row++) {
        const rowMeta = excelData.rowMetadata[row];
        if (rowMeta && rowMeta.isMarked) {
          const eventNameCellAddress = XLSX.utils.encode_cell({ r: row, c: 1 });
          if (worksheet[eventNameCellAddress]) {
            worksheet[eventNameCellAddress].s = {
              ...worksheet[eventNameCellAddress].s,
              fill: { fgColor: { rgb: "E8F5E8" } }, // Light green background
              font: { ...worksheet[eventNameCellAddress].s?.font, color: { rgb: "000000" } }
            };
          }
        }
      }

      // Set number formatting for revenue columns
      for (let row = 1; row <= range.e.r; row++) {
        // Predicted Revenue column (column 2)
        const predictedCellAddress = XLSX.utils.encode_cell({ r: row, c: 2 });
        if (worksheet[predictedCellAddress] && typeof worksheet[predictedCellAddress].v === 'number') {
          worksheet[predictedCellAddress].z = '#,##0';
        }
        
        // Actual Revenue column (column 3)
        const actualCellAddress = XLSX.utils.encode_cell({ r: row, c: 3 });
        if (worksheet[actualCellAddress] && typeof worksheet[actualCellAddress].v === 'number') {
          worksheet[actualCellAddress].z = '#,##0';
        }
      }

      // Add the worksheet to workbook
      const sheetName = `Revenue Summary ${filterType === 'month' ? 'Month' : 'Week'}`;
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

      // Write the file
      XLSX.writeFile(workbook, excelFilename);
      
      message.success('Excel file exported successfully!');
    } catch (error) {
      console.error('Export error:', error);
      message.error('Failed to export Excel file');
    }
  }, [excelData, excelFilename, filterType]);

  // Table columns with rowspan logic
  const columns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: '25%',
      render: (text: string, record: SummaryTableData) => ({
        children: text,
        props: {
          rowSpan: record.dateRowSpan,
        },
      }),
    },
    {
      title: 'Events',
      dataIndex: 'eventName',
      key: 'eventName',
      width: '35%',
      render: (text: string, record: SummaryTableData) => ({
        children: text,
        props: {
          style: record.isMarked ? { backgroundColor: '#d8f5e1', color: '#000' } : {},
        },
      }),
    },
    {
      title: (
        <div>
          <div>Predicted Revenue</div>
          <div style={{ fontSize: '12px', fontWeight: 'normal', color: '#1890ff' }}>
            Total: {sumValues.totalPredicted.toLocaleString()}
          </div>
        </div>
      ),
      dataIndex: 'predictedRevenue',
      key: 'predictedRevenue',
      width: '20%',
      align: 'right' as const,
      render: (value: number, record: SummaryTableData) => ({
        children: value.toLocaleString(),
        props: {
          rowSpan: record.revenueRowSpan,
        },
      }),
    },
    {
      title: (
        <div>
          <div>Actual Revenue (TopUp)</div>
          <div style={{ fontSize: '12px', fontWeight: 'normal', color: '#52c41a' }}>
            Total: {sumValues.totalActual.toLocaleString()}
          </div>
          {progressionData && (
            <div style={{ fontSize: '11px', fontWeight: 'normal', color: '#1890ff', marginTop: '2px' }}>
              Target: {progressionData.targetRevenue.toLocaleString()} 
              {targetLoading ? (
                <span style={{ marginLeft: '4px' }}>Loading...</span>
              ) : (
                <span style={{ 
                  marginLeft: '4px',
                  color: progressionData.percentage >= 100 ? '#52c41a' : progressionData.percentage >= 75 ? '#faad14' : '#ff4d4f'
                }}>
                  ({progressionData.percentage}%)
                </span>
              )}
            </div>
          )}
        </div>
      ),
      dataIndex: 'actualRevenue',
      key: 'actualRevenue',
      width: '20%',
      align: 'right' as const,
      render: (value: number, record: SummaryTableData) => {
        const percentage = record.predictedRevenue > 0 
          ? ((value / record.predictedRevenue) * 100)
          : 0;
        
        const percentageColor = percentage >= 100 ? '#52c41a' : percentage >= 75 ? '#faad14' : '#ff4d4f';
        
        return {
          children: (
            <div>
              <div>{value.toLocaleString()}</div>
              <div style={{ 
                fontSize: '11px', 
                color: percentageColor,
                fontWeight: 'normal',
                marginTop: '2px'
              }}>
                ({Math.round(percentage * 100) / 100}%)
              </div>
            </div>
          ),
          props: {
            rowSpan: record.revenueRowSpan,
          },
        };
      },
    },
  ];

  const handleDateChange = (date: Dayjs | null) => {
    if (date) {
      if (filterType === 'week') {
        // For week filter, set selectedDate to the first day of the selected week (Monday)
        setSelectedDate(date.startOf('isoWeek'));
      } else {
        setSelectedDate(date);
      }
    }
  };

  return (
    <Modal
      title="Revenue Summary & Target Management"
      open={visible}
      onCancel={onClose}
      width={1200}
      footer={null}
      destroyOnClose
    >
      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'summary',
            label: (
              <span>
                <CalendarOutlined />
                Revenue Summary
              </span>
            ),
            children: (
              <Space direction="vertical" style={{ width: '100%' }} size="large">
                {/* Filter Controls */}
                <Card size="small">
                  <Row gutter={16} align="middle">
                    <Col span={8}>
                      <Space>
                        <CalendarOutlined />
                        <Title level={5} style={{ margin: 0 }}>Filter Period:</Title>
                      </Space>
                    </Col>
                    <Col span={8}>
                      <Radio.Group
                        value={filterType}
                        onChange={(e) => {
                          const newFilterType = e.target.value;
                          setFilterType(newFilterType);
                          
                          // If switching to week mode, adjust selectedDate to Monday
                          if (newFilterType === 'week') {
                            setSelectedDate(selectedDate.startOf('isoWeek'));
                          }
                        }}
                        buttonStyle="solid"
                      >
                        <Radio.Button value="month">Month</Radio.Button>
                        <Radio.Button value="week">Week</Radio.Button>
                      </Radio.Group>
                    </Col>
                    <Col span={7}>
                      <DatePicker
                        value={selectedDate}
                        onChange={handleDateChange}
                        picker={getDatePickerPicker()}
                        format={getDatePickerFormat()}
                        style={{ width: '100%' }}
                        allowClear={false}
                      />
                    </Col>
                     <Col span={1}>
                       <Tooltip title="Export to Excel">
                         <Button
                           type="primary"
                           icon={<FileExcelOutlined />}
                           onClick={handleExcelExport}
                         />
                       </Tooltip>
                     </Col>
                  </Row>
                  
                  <Row style={{ marginTop: 12 }}>
                    <Col span={24}>
                      <Typography.Text type="secondary">
                        Showing data for: <strong>{getDateRangeText()}</strong> 
                        {filteredEvents.length > 0 && (
                          <span> • {filteredEvents.length} event(s) found</span>
                        )}
                        {topupData.length > 0 && (
                          <span> • TopUp data loaded from cache</span>
                        )}
                      </Typography.Text>
                    </Col>
                  </Row>
                </Card>

                {/* Summary Table */}
                <Table
                  columns={columns}
                  dataSource={tableData}
                  pagination={false}
                  bordered
                  size="small"
                  scroll={{ y: 400 }}
                  style={{ minHeight: 400 }}
                />
              </Space>
            ),
          },
          {
            key: 'target',
            label: (
              <span>
                <AimOutlined />
                Target Setup
              </span>
            ),
            children: <TargetSetupTab />,
          },
        ]}
      />
    </Modal>
  );
};

export default SummaryModal;
